import { Extension, type Editor } from '@tiptap/core'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'

export type ImageUploader = (file: File) => Promise<string>
export type ImageImporter = (url: string) => Promise<string>
/** Larghezza naturale dell'immagine in `src`, o null se non misurabile. */
export type ImageMeasurer = (src: string) => Promise<number | null>

/**
 * Tetto della larghezza di default delle immagini incollate/trascinate (#263):
 * uno screenshot retina arriverebbe a piena dimensione nell'email.
 */
export const MAX_DEFAULT_IMAGE_WIDTH = 300

/** Import per paste (#262): le immagini remote oltre il tetto vengono tolte e segnalate. */
export const MAX_IMPORTS_PER_PASTE = 10
const IMPORT_CONCURRENCY = 3
/** Marca le immagini remote di un paste finché il loro import non è concluso. */
const IMPORT_ID_ATTR = 'data-import-id'

/** Immagini di un paste: le remote da importare, marcate con `id`. */
interface PastedBatch {
  id: string
  urls: string[]
  removed: number
}

export interface ImageUploadOptions {
  /** Carica il file e restituisce l'URL da usare come src. */
  upload: ImageUploader | null
  onError: (error: unknown) => void
  /** Importa sul nostro storage un'immagine http(s) esterna e ne restituisce l'URL (#262). */
  importRemote: ImageImporter | null
  /** src già sul nostro storage: resta com'è. */
  isStored: (src: string) => boolean
  /** Immagini incollate non recuperabili (file:, cid:, import fallito) tolte dal contenuto. */
  onRemoved: (count: number) => void
  /** Misura le immagini senza width per dar loro quella di default; null = nessun default (#263). */
  measureWidth: ImageMeasurer | null
  /**
   * Riceve ogni lavoro asincrono di paste/drop (upload o import, fino
   * all'inserimento nel documento): chi usa l'editor ne tiene il conteggio (#263).
   */
  track: (work: Promise<void>) => Promise<void>
}

/**
 * Incolla/trascina di immagini nell'editor firma (#250).
 *
 * Lo schema firma rifiuta i data URI (allowBase64: false: il base64 gonfia
 * l'email e non passa dal CID). Senza questa estensione uno screenshot
 * incollato o un'immagine trascinata sparivano in silenzio: qui vengono
 * caricati sull'endpoint di upload e inseriti con l'URL restituito.
 */
export const ImageUpload = Extension.create<ImageUploadOptions>({
  name: 'imageUpload',

  addOptions() {
    return {
      upload: null,
      onError: () => {},
      importRemote: null,
      isStored: () => false,
      onRemoved: () => {},
      measureWidth: null,
      track: (work) => work,
    }
  },

  // Marca temporanea delle immagini incollate in attesa di import: mai
  // serializzata, serve solo a ritrovare quei nodi e non altri con lo stesso src.
  addGlobalAttributes() {
    return [
      {
        types: ['image'],
        attributes: {
          importId: {
            default: null,
            keepOnSplit: false,
            parseHTML: (element) => element.getAttribute(IMPORT_ID_ATTR),
            renderHTML: () => ({}),
          },
        },
      },
    ]
  },

  addProseMirrorPlugins() {
    const { editor, options } = this
    const upload = options.upload
    if (!upload) return []

    const defaultWidth = (src: string) => defaultImageWidth(src, options.measureWidth)

    // Upload in sequenza: le immagini compaiono nell'ordine di paste/drop,
    // ognuna subito dopo la precedente.
    const insert = async (files: File[], pos?: number) => {
      let at = pos
      for (const file of files) {
        try {
          const src = await upload(file)
          const width = await defaultWidth(src)
          if (editor.isDestroyed) return
          at = insertImage(editor, src, at, width)
        } catch (error) {
          options.onError(error)
        }
      }
    }

    const classify = (src: string) => classifyImageSrc(src, options)

    // transformPastedHTML (che toglie le immagini perse e marca le remote)
    // precede sempre handlePaste/handleDrop dello stesso paste: il batch
    // passa dall'uno all'altro qui.
    let pasteSeq = 0
    let pendingBatch: PastedBatch | null = null

    const preparePastedHtml = (html: string): string => {
      const { html: prepared, batch } = prepareImages(html, classify, `paste-${++pasteSeq}`)
      pendingBatch = batch
      return prepared
    }

    // Le immagini remote sono già nel documento (paste di default) col link
    // originale: a import finito ne sostituiamo il src, o le togliamo se fallisce.
    // Un solo avviso per paste, con tutte le immagini perse.
    const importRemoteImages = async ({ id, urls, removed }: PastedBatch) => {
      const importer = options.importRemote
      const lost = await mapWithConcurrency(urls, IMPORT_CONCURRENCY, async (url) => {
        let src: string | null = null
        if (importer) {
          try {
            src = await importer(url)
          } catch (error) {
            console.error('Import immagine incollata fallito', url, error)
          }
        }
        const width = src ? await defaultWidth(src) : null
        return editor.isDestroyed ? 0 : settlePastedImage(editor, id, url, src, width)
      })
      const total = lost.reduce((sum, n) => sum + n, removed)
      if (total && !editor.isDestroyed) options.onRemoved(total)
    }

    // Paste e drop condividono la stessa regola: file immagine da soli → li
    // gestiamo noi; HTML con testo → il testo segue il flusso di default, le
    // immagini base64 (che lo schema scarterebbe) vengono caricate e quelle
    // remote importate (#262).
    const handleTransfer = (data: DataTransfer | null, pos?: number): boolean => {
      const batch = pendingBatch
      pendingBatch = null
      if (!data) return false
      const html = data.getData('text/html')
      const files = imageFilesFrom(data.files)
      const hasText = htmlHasText(html)
      if (files.length && !hasText) {
        void options.track(insert(files, pos))
        return true
      }
      const embedded = embeddedImagesFrom(html, classify)
      if (embedded.length) void options.track(insert(embedded, pos))
      if (batch && (batch.urls.length || batch.removed)) void options.track(importRemoteImages(batch))
      // Solo immagini base64 senza testo né link: il default non inserirebbe nulla.
      return embedded.length > 0 && !hasText && !batch?.urls.length
    }

    return [
      new Plugin({
        key: new PluginKey('imageUpload'),
        props: {
          // file: e cid: (Outlook desktop, Gmail) non sono leggibili dal
          // browser: fuori dal contenuto incollato, l'avviso parte da handleTransfer.
          transformPastedHTML: preparePastedHtml,

          handlePaste: (_view, event) => handleTransfer(event.clipboardData),

          handleDrop: (view, event, _slice, moved) => {
            if (moved) return false
            const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos
            const handled = handleTransfer(event.dataTransfer, pos)
            if (handled) event.preventDefault()
            return handled
          },
        },
      }),
    ]
  },
})

/**
 * Width di default di un'immagine appena caricata: min(larghezza naturale,
 * tetto). Una misura fallita non blocca l'inserimento: niente width (#263).
 */
export async function defaultImageWidth(
  src: string,
  measure: ImageMeasurer | null,
): Promise<number | null> {
  const natural = await measure?.(src).catch(() => null)
  return natural ? Math.min(natural, MAX_DEFAULT_IMAGE_WIDTH) : null
}

/**
 * Porta tutte le immagini a `width` in un'unica transazione, senza toccare gli
 * altri attributi: un import in corso ritrova la sua immagine dall'importId (#263).
 */
export function resizeAllImages(editor: Editor, width: number): void {
  const { tr } = editor.state
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'image') tr.setNodeMarkup(pos, undefined, { ...node.attrs, width })
  })
  if (tr.docChanged) editor.view.dispatch(tr)
}

/**
 * Inserisce l'immagine in `pos` (drop) o al posto della selezione (paste) e
 * restituisce la posizione subito dopo, dove va la prossima immagine.
 */
function insertImage(editor: Editor, src: string, pos: number | undefined, width: number | null): number {
  const { state } = editor
  const image = state.schema.nodes.image.create({ src, width })
  // Il documento può essere cambiato durante l'upload: la posizione va limitata.
  const from = pos === undefined ? state.selection.from : Math.min(pos, state.doc.content.size)
  const to = pos === undefined ? state.selection.to : from
  const tr = state.tr.replaceRangeWith(from, to, image)
  const after = tr.mapping.map(to)
  tr.setSelection(TextSelection.near(tr.doc.resolve(after)))
  editor.view.dispatch(tr.scrollIntoView())
  return after
}

/**
 * Conclude l'import di `src` per le sole immagini del paste `importId`: le
 * porta a `next`, o le rimuove se `next` è null. Restituisce quante ne ha rimosse.
 * `width` vale solo per le immagini incollate senza una width propria (#263).
 * Fuori dalla history: Ctrl+Z non deve riportare il link esterno.
 */
function settlePastedImage(
  editor: Editor,
  importId: string,
  src: string,
  next: string | null,
  width: number | null,
): number {
  const { state } = editor
  const tr = state.tr
  let count = 0
  state.doc.descendants((node, pos) => {
    if (node.type.name !== 'image') return
    if (node.attrs.importId !== importId || node.attrs.src !== src) return
    count++
    const from = tr.mapping.map(pos)
    if (next) {
      tr.setNodeMarkup(from, undefined, {
        ...node.attrs,
        src: next,
        importId: null,
        width: node.attrs.width ?? width,
      })
    } else {
      tr.delete(from, from + node.nodeSize)
    }
  })
  if (!tr.docChanged) return 0
  editor.view.dispatch(tr.setMeta('addToHistory', false))
  return next ? 0 : count
}

/** Esegue `fn` su tutti gli elementi, al massimo `limit` alla volta. */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = []
  let next = 0
  const worker = async () => {
    while (next < items.length) {
      const index = next++
      results[index] = await fn(items[index])
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
  return results
}

type ImageSrcKind = 'embedded' | 'kept' | 'remote' | 'broken'

function classifyImageSrc(
  src: string,
  { isStored, importRemote }: Pick<ImageUploadOptions, 'isStored' | 'importRemote'>,
): ImageSrcKind {
  if (/^data:/i.test(src)) return 'embedded'
  if (isStored(src)) return 'kept'
  // Senza import configurato il link esterno resta com'era.
  if (/^https?:\/\//i.test(src)) return importRemote ? 'remote' : 'kept'
  return 'broken'
}

function embeddedImagesFrom(html: string, classify: (src: string) => ImageSrcKind): File[] {
  const embedded: File[] = []
  imagesIn(html).forEach((img) => {
    const src = img.getAttribute('src') ?? ''
    if (classify(src) !== 'embedded') return
    const file = dataUriToFile(src, `pasted-image-${embedded.length + 1}`)
    if (file) embedded.push(file)
  })
  return embedded
}

/**
 * Toglie dall'HTML incollato le immagini perse (file:, cid:, remote oltre il
 * tetto) e marca con `id` le remote da importare.
 */
function prepareImages(
  html: string,
  classify: (src: string) => ImageSrcKind,
  id: string,
): { html: string; batch: PastedBatch } {
  const batch: PastedBatch = { id, urls: [], removed: 0 }
  if (!/<img/i.test(html)) return { html, batch }
  const doc = parseHtml(html)
  let changed = false
  doc.querySelectorAll('img[src]').forEach((img) => {
    const src = img.getAttribute('src') ?? ''
    const kind = classify(src)
    if (kind === 'remote' && !batch.urls.includes(src) && batch.urls.length < MAX_IMPORTS_PER_PASTE) {
      batch.urls.push(src)
    }
    if (kind === 'remote' && batch.urls.includes(src)) {
      img.setAttribute(IMPORT_ID_ATTR, id)
    } else if (kind === 'remote' || kind === 'broken') {
      img.remove()
      batch.removed++
    } else {
      return
    }
    changed = true
  })
  return { html: changed ? doc.body.innerHTML : html, batch }
}

function imagesIn(html: string): Element[] {
  if (!/<img/i.test(html)) return []
  return Array.from(parseHtml(html).querySelectorAll('img[src]'))
}

function parseHtml(html: string): Document {
  return new DOMParser().parseFromString(html, 'text/html')
}

function imageFilesFrom(files: FileList | File[] | undefined | null): File[] {
  return Array.from(files ?? []).filter((file) => file.type.startsWith('image/'))
}

function htmlHasText(html: string): boolean {
  if (!html) return false
  return Boolean(parseHtml(html).body.textContent?.trim())
}

/** Decodifica un data URI immagine base64 in un File caricabile. */
export function dataUriToFile(dataUri: string, baseName: string): File | null {
  // Parametri opzionali prima di ;base64 (es. data:image/png;name=logo.png;base64,...)
  const match =
    /^data:(image\/([\w.-]+)(?:\+[\w.-]+)?)(?:;(?!base64,)[^;,]*)*;base64,([\s\S]*)$/i.exec(
      dataUri,
    )
  if (!match) return null
  const [, rawMimeType, rawSubtype, payload] = match
  const mimeType = rawMimeType.toLowerCase()
  const subtype = rawSubtype.toLowerCase()
  try {
    const binary = atob(payload.replace(/\s/g, ''))
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
    return new File([bytes], `${baseName}.${subtype}`, { type: mimeType })
  } catch {
    return null
  }
}
